import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, API_BASE } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { formatSaturday, formatTimeRange } from "../lib/fmt";
import Header from "../components/Header";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { Calendar, Clock, MapPin, CheckCircle2, Copy, ExternalLink,
  Sparkles, CalendarPlus, Truck, ClipboardList, PackageOpen, UserCheck,
  CalendarRange, X,
} from "lucide-react";
import BulkSignupDialog from "../components/BulkSignupDialog";

const ROLE_ICON = {
  delivery_driver: Truck,
  delivery_coordinator: ClipboardList,
  pickup_driver: PackageOpen,
};

function monthKeyOf(iso) {
  if (!iso) return "";
  return iso.slice(0, 7);
}
function monthLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso + "-01T12:00:00");
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
}

function RoleRow({ shift, roleKey, roleData, onClaim, alreadyInThisRole, alreadyInOtherRole }) {
  const Icon = ROLE_ICON[roleKey] || Truck;
  const isFull = roleData.open === 0;
  const disabled = isFull || alreadyInThisRole || alreadyInOtherRole;
  let label;
  if (alreadyInThisRole) label = "You're in";
  else if (alreadyInOtherRole) label = "1 role / day";
  else if (isFull) label = "Filled";
  else label = "Sign up";
  return (
    <div className="flex items-center gap-3 py-2.5 border-t border-slate-100 first:border-t-0">
      <div className={`p-2 rounded-lg ${isFull ? "bg-slate-100 text-slate-400" : "bg-brand-blue/10 text-brand-blue"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-slate-900 text-sm">{roleData.label}</div>
        <div className="text-xs text-slate-500">
          {isFull ? (
            <>Filled by <span className="text-slate-700 font-medium">{roleData.signups[0]?.name}</span></>
          ) : (
            <>{roleData.open} of {roleData.slots} open</>
          )}
        </div>
      </div>
      <Button
        data-testid={`claim-${shift.id}-${roleKey}`}
        size="sm"
        disabled={disabled}
        onClick={() => onClaim({ shift, roleKey, roleData })}
        className={`min-h-[40px] px-4 font-semibold text-sm ${
          alreadyInThisRole
            ? "bg-brand-green/15 text-brand-green-dark hover:bg-brand-green/20"
            : "bg-brand-blue hover:bg-brand-blue-dark text-white"
        } disabled:opacity-50 disabled:cursor-not-allowed`}
      >
        {label}
      </Button>
    </div>
  );
}

function ShiftCard({ shift, onClaim, mySignupRoleKeys }) {
  const alreadyInShift = Object.keys(shift.roles).some((rk) => mySignupRoleKeys.has(`${shift.id}::${rk}`));
  return (
    <div data-testid={`shift-card-${shift.id}`} className="card-elevated rounded-xl p-5 sm:p-6 animate-fade-up">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900">
            {shift.route?.name || "Route"}
          </h3>
          {shift.route?.description && (
            <p className="text-sm text-slate-500 mt-1 leading-relaxed line-clamp-2">{shift.route.description}</p>
          )}
        </div>
        <span
          data-testid={`shift-status-${shift.id}`}
          className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold uppercase tracking-wider ${
            shift.open === 0
              ? "bg-brand-green/15 text-brand-green-dark"
              : "bg-amber-100 text-amber-700"
          }`}
        >
          {shift.open === 0 ? "All filled" : `${shift.open} open`}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600 mb-3">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-slate-400 shrink-0" /><span>{formatTimeRange(shift.start_time, shift.end_time)}</span>
        </div>
        <div className="flex items-center gap-2 min-w-0">
          <MapPin className="w-4 h-4 text-slate-400 shrink-0" /><span className="truncate" title={shift.pickup_location}>{shift.pickup_location}</span>
        </div>
      </div>
      <div className="rounded-lg border border-slate-100 bg-slate-50/50 px-3">
        {Object.entries(shift.roles).map(([roleKey, roleData]) => {
          const alreadyInThisRole = mySignupRoleKeys.has(`${shift.id}::${roleKey}`);
          return (
            <RoleRow
              key={roleKey}
              shift={shift}
              roleKey={roleKey}
              roleData={roleData}
              alreadyInThisRole={alreadyInThisRole}
              alreadyInOtherRole={alreadyInShift && !alreadyInThisRole}
              onClaim={onClaim}
            />
          );
        })}
      </div>
    </div>
  );
}

function ClaimDialog({ ctx, open, onClose, onSuccess, user }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [smsOptIn, setSmsOptIn] = useState(true);  // Q1=c — pre-toggled ON
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setName(user?.name || "");
      setEmail(user?.email || "");
      setPhone(user?.phone || "");
      setSmsOptIn(true);
    }
  }, [open, user]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim() || !phone.trim()) return toast.error("Please fill out all fields.");
    setSubmitting(true);
    try {
      const { data } = await api.post("/public/signups", {
        shift_id: ctx.shift.id,
        role: ctx.roleKey,
        name, email, phone,
        sms_opt_in: smsOptIn,
      });
      onSuccess(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not sign up.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!ctx) return null;
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="claim-dialog" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Sign up as {ctx.roleData.label}</DialogTitle>
          <DialogDescription>
            {ctx.shift.route?.name} · {formatSaturday(ctx.shift.date)} · {formatTimeRange(ctx.shift.start_time, ctx.shift.end_time)}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2">
          <div>
            <Label htmlFor="claim-name" className="text-sm font-semibold">Full name</Label>
            <Input id="claim-name" data-testid="claim-name-input" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 text-base min-h-[44px]" />
          </div>
          <div>
            <Label htmlFor="claim-email" className="text-sm font-semibold">Email</Label>
            <Input id="claim-email" data-testid="claim-email-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1.5 text-base min-h-[44px]" />
          </div>
          <div>
            <Label htmlFor="claim-phone" className="text-sm font-semibold">Mobile phone</Label>
            <Input id="claim-phone" data-testid="claim-phone-input" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required className="mt-1.5 text-base min-h-[44px]" />
          </div>
          <label className="flex items-start gap-2.5 cursor-pointer rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 px-3 py-2.5 transition-colors focus-within:ring-2 focus-within:ring-brand-blue">
            <input
              type="checkbox"
              data-testid="claim-sms-optin"
              checked={smsOptIn}
              onChange={(e) => setSmsOptIn(e.target.checked)}
              className="mt-0.5 w-4 h-4 rounded border-slate-300 accent-brand-blue"
            />
            <span className="text-sm text-slate-700 leading-snug">
              <span className="font-semibold text-slate-900">Also text me Saturday reminders</span>
              <br />
              <span className="text-xs text-slate-500">A confirmation now and a reminder on Wednesday. Reply STOP to opt out any time.</span>
            </span>
          </label>
          <Button type="submit" data-testid="claim-submit-button" disabled={submitting} className="w-full min-h-[48px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold text-base rounded-lg">
            {submitting ? "Claiming…" : `Confirm ${ctx.roleData.label}`}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ConfirmationDialog({ payload, open, onClose, onBackToBoard }) {
  const [showLink, setShowLink] = useState(false);
  if (!payload) return null;
  const editUrl = `${window.location.origin}/my-signup/${payload.edit_token}`;
  const firstName = ((payload.signup?.name || payload.name || "").split(" ")[0]) || "there";
  const copy = () => { navigator.clipboard.writeText(editUrl); toast.success("Link copied!"); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="confirmation-dialog" className="sm:max-w-md">
        <DialogHeader>
          <div className="mx-auto h-14 w-14 rounded-full bg-brand-green/15 text-brand-green-dark flex items-center justify-center mb-1">
            <CheckCircle2 className="w-7 h-7" />
          </div>
          <DialogTitle className="font-heading text-2xl text-center">See you Saturday, {firstName}!</DialogTitle>
          <DialogDescription className="text-center">
            Your spot is saved. We&apos;ll email you a reminder the Wednesday before.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 rounded-lg bg-slate-50 border border-slate-200 p-3.5 text-sm text-slate-700 leading-relaxed">
          <span className="font-semibold">Check your inbox.</span> We just sent a confirmation email — click the link in it any time to cancel or swap. Nothing to remember or save here.
        </div>

        <div className="space-y-2.5 mt-2">
          <Button
            type="button"
            onClick={onBackToBoard}
            data-testid="confirm-back-to-board-button"
            className="w-full min-h-[52px] bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold text-base"
          >
            Back to Sign Ups
          </Button>
          <a
            href={`${API_BASE}/public/signups/${payload.edit_token}/calendar.ics`}
            data-testid="confirm-add-to-calendar-link"
            className="inline-flex items-center justify-center w-full min-h-[48px] rounded-md border-2 border-brand-green/40 bg-brand-green/10 text-brand-green-dark hover:bg-brand-green/20 font-semibold text-sm transition-colors focus-ring px-3 py-2"
          >
            <CalendarPlus className="w-4 h-4 mr-1.5" /> Add to my calendar
          </a>
          <div className="flex items-center justify-between gap-3 pt-1">
            <Link
              to={`/my-signup/${payload.edit_token}`}
              data-testid="open-signup-link"
              className="text-sm text-brand-blue font-semibold hover:underline focus-ring rounded"
            >
              View my Saturdays →
            </Link>
            <button
              type="button"
              onClick={() => setShowLink((v) => !v)}
              data-testid="confirm-toggle-advanced"
              className="text-xs text-slate-500 hover:text-slate-700 underline decoration-dotted underline-offset-2 focus-ring rounded"
            >
              {showLink ? "Hide link" : "Show my edit link"}
            </button>
          </div>
          {showLink && (
            <div data-testid="confirm-advanced-panel" className="rounded-md border border-slate-200 bg-white p-3 space-y-2">
              <p className="text-xs text-slate-500">Personal link (also sent by email — no password needed):</p>
              <p className="text-xs break-all text-slate-700 font-mono leading-relaxed">{editUrl}</p>
              <Button type="button" onClick={copy} data-testid="copy-edit-link-button" variant="outline" size="sm" className="w-full">
                <Copy className="w-3.5 h-3.5 mr-1.5" /> Copy link
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function MonthTabs({ months, activeMonth, onJump }) {
  const stripRef = useRef(null);
  const tabRefs = useRef({});

  // Auto-scroll the active tab into view within the strip whenever it changes
  // — so the highlighted month is always visible on a narrow phone screen.
  useEffect(() => {
    const el = tabRefs.current[activeMonth];
    if (!el || !stripRef.current) return;
    const strip = stripRef.current;
    const elLeft = el.offsetLeft;
    const elRight = elLeft + el.offsetWidth;
    const stripLeft = strip.scrollLeft;
    const stripRight = stripLeft + strip.clientWidth;
    if (elLeft < stripLeft + 8) {
      strip.scrollTo({ left: Math.max(0, elLeft - 16), behavior: "smooth" });
    } else if (elRight > stripRight - 8) {
      strip.scrollTo({ left: elRight - strip.clientWidth + 16, behavior: "smooth" });
    }
  }, [activeMonth]);

  if (!months || months.length < 2) return null;
  return (
    <div data-testid="month-tabs" className="sticky top-[60px] z-30 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-background/95 backdrop-blur border-b border-slate-200">
      <div
        ref={stripRef}
        className="flex gap-1.5 overflow-x-auto no-scrollbar"
        style={{ WebkitOverflowScrolling: "touch", scrollbarWidth: "none", touchAction: "pan-x" }}
      >
        {months.map((m) => (
          <button
            key={m}
            ref={(el) => { if (el) tabRefs.current[m] = el; }}
            data-testid={`month-tab-${m}`}
            onClick={() => onJump(m)}
            data-active={activeMonth === m}
            className={`shrink-0 px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider transition-colors focus-ring ${
              activeMonth === m
                ? "bg-brand-blue text-white shadow-sm"
                : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {monthLabel(m)}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PublicBoard() {
  const { user } = useAuth();
  const [board, setBoard] = useState(null);
  const [mySignups, setMySignups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claimCtx, setClaimCtx] = useState(null);
  const [confirm, setConfirm] = useState(null);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [activeMonth, setActiveMonth] = useState("");
  const [roleFilter, setRoleFilter] = useState("all"); // all | delivery_driver | delivery_coordinator | pickup_driver
  const [showOnboarding, setShowOnboarding] = useState(false);
  const monthRefs = useRef({});
  const mySignupsSectionRef = useRef(null);

  useEffect(() => {
    // Show the first-visit onboarding banner once per browser.
    if (typeof window !== "undefined" && !window.localStorage.getItem("h2h_onboarded")) {
      setShowOnboarding(true);
    }
  }, []);

  const dismissOnboarding = () => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("h2h_onboarded", "1");
    }
    setShowOnboarding(false);
  };

  const scrollToMySignups = () => {
    // Give React a beat to render the just-added signup card before we scroll.
    setTimeout(() => {
      mySignupsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 80);
  };

  const load = async () => {
    setLoading(true);
    try {
      const [b, mine] = await Promise.all([
        api.get("/public/board"),
        api.get("/public/my-signups"),
      ]);
      setBoard(b.data);
      setMySignups(mine.data || []);
    } catch {
      toast.error("Couldn't load shifts. Please refresh.");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const handleSuccess = (payload) => { setClaimCtx(null); setConfirm(payload); load(); };
  const mySignupShiftIds = useMemo(() => new Set(mySignups.map((m) => m.shift?.id)), [mySignups]);
  const mySignupRoleKeys = useMemo(
    () => new Set(mySignups.map((m) => `${m.shift?.id}::${m.signup?.role}`)),
    [mySignups],
  );

  const months = useMemo(() => {
    if (!board?.days) return [];
    const set = new Set(board.days.map((d) => monthKeyOf(d.date)));
    return Array.from(set).sort();
  }, [board]);

  useEffect(() => {
    if (months.length && !activeMonth) setActiveMonth(months[0]);
  }, [months, activeMonth]);

  // Scroll-spy: highlight the month tab whose section is currently in view.
  useEffect(() => {
    if (!months.length) return;
    const observer = new IntersectionObserver(
      (entries) => {
        // Pick the entry with the largest intersection ratio that's intersecting.
        const visible = entries.filter((e) => e.isIntersecting);
        if (!visible.length) return;
        visible.sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const id = visible[0].target.dataset.month;
        if (id) setActiveMonth(id);
      },
      {
        // Account for sticky header (~64px) + month tabs strip (~52px)
        rootMargin: "-120px 0px -55% 0px",
        threshold: [0, 0.25, 0.5, 0.75, 1],
      },
    );
    months.forEach((m) => {
      const el = monthRefs.current[m];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [months]);

  const jumpToMonth = (m) => {
    setActiveMonth(m);
    const el = monthRefs.current[m];
    if (!el) return;
    // scrollIntoView with scroll-margin-top CSS is the most reliable cross-browser
    // approach (works on iOS Safari, where smooth window.scrollTo can be flaky
    // when stacked sticky elements are present).
    el.scrollIntoView({ block: "start", behavior: "smooth" });
    // Belt-and-suspenders fallback for older iOS that ignores `behavior:smooth`:
    setTimeout(() => {
      const offset = 120;
      const rect = el.getBoundingClientRect();
      // If the section isn't near top-of-viewport, force-jump.
      if (rect.top < offset - 40 || rect.top > offset + 40) {
        const y = rect.top + window.pageYOffset - offset;
        window.scrollTo(0, y);
      }
    }, 450);
  };

  return (
    <div className="min-h-screen surface-public">
      <Header variant="public" />
      {showOnboarding && (
        <div data-testid="onboarding-banner" className="bg-brand-blue/10 border-b border-brand-blue/20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-start sm:items-center gap-3">
            <div className="shrink-0 rounded-full bg-white text-brand-blue p-1.5 shadow-sm">
              <Sparkles className="w-4 h-4" />
            </div>
            <p className="flex-1 text-sm text-slate-700 leading-snug">
              <span className="font-semibold text-slate-900">New here?</span>{" "}
              Just tap a role on any Saturday to sign up — it takes about 15 seconds. We&apos;ll email you a link so you can cancel or swap later.
            </p>
            <button
              onClick={dismissOnboarding}
              data-testid="onboarding-dismiss"
              className="shrink-0 rounded-full p-1.5 text-slate-500 hover:bg-white hover:text-slate-900 focus-ring"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      <section className="relative">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 sm:pt-14 pb-4 text-center">
          <span className="inline-block text-xs uppercase tracking-[0.22em] font-semibold text-brand-blue mb-3">
            Saturday Volunteers
          </span>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight">
            {user?.name ? `Hi ${user.name.split(" ")[0]} — ` : ""}Pick a Saturday.
            <br className="hidden sm:block" />
            <span className="text-brand-blue"> Pick your role.</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-600 mt-4 max-w-xl mx-auto leading-relaxed">
            Each Saturday needs a Delivery Driver, a Delivery Coordinator, and a Pick Up Driver. Claim the role that fits you.
          </p>
          <div className="mt-6 flex justify-center">
            <Button
              data-testid="open-bulk-signup-button"
              onClick={() => setBulkOpen(true)}
              className="min-h-[48px] px-6 bg-brand-blue hover:bg-brand-blue-dark text-white font-semibold text-sm sm:text-base rounded-full shadow-sm"
            >
              <CalendarRange className="w-4 h-4 mr-2" />
              Sign up for multiple Saturdays
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        <MonthTabs months={months} activeMonth={activeMonth} onJump={jumpToMonth} />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 mt-3 mb-2">
        <div className="flex flex-wrap items-center gap-2" role="tablist" aria-label="Filter by role">
          {[
            { k: "all", label: "All roles", Icon: Sparkles },
            { k: "delivery_driver", label: "Delivery Driver", Icon: Truck },
            { k: "delivery_coordinator", label: "Coordinator", Icon: ClipboardList },
            { k: "pickup_driver", label: "Pick Up Driver", Icon: PackageOpen },
          ].map(({ k, label, Icon }) => {
            const active = roleFilter === k;
            return (
              <button
                key={k}
                data-testid={`role-filter-${k}`}
                onClick={() => setRoleFilter(k)}
                className={`inline-flex items-center gap-1.5 rounded-full border-2 px-3 py-1.5 text-xs font-semibold transition-colors focus-ring ${
                  active
                    ? "bg-brand-blue text-white border-brand-blue"
                    : "bg-white text-slate-700 border-slate-200 hover:border-brand-blue hover:text-brand-blue"
                }`}
              >
                <Icon className="w-3.5 h-3.5" /> {label}
              </button>
            );
          })}
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-24 space-y-10">
        {mySignups.length > 0 && (
          <section data-testid="my-signups-section" ref={mySignupsSectionRef} className="space-y-3">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-brand-green-dark" />
              <h2 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900">Your Saturday slots</h2>
            </div>
            <div className="grid gap-3">
              {mySignups.map((m) => (
                <div key={m.signup.id} data-testid={`my-signup-${m.signup.id}`} className="card-elevated rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                  <Link to={`/my-signup/${m.signup.edit_token}`} className="flex-1 min-w-0 focus-ring rounded-md">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-semibold text-slate-900">{m.route?.name}</span>
                      <span className="px-2 py-0.5 rounded-full bg-brand-blue/10 text-brand-blue text-[10px] uppercase tracking-wider font-bold inline-flex items-center gap-1">
                        <UserCheck className="w-3 h-3" /> {m.signup.role_label}
                      </span>
                    </div>
                    <div className="text-xs text-slate-500 mt-1 truncate">
                      {formatSaturday(m.shift?.date)} · {m.shift?.start_time}–{m.shift?.end_time}
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 shrink-0">
                    <a
                      href={`${API_BASE}/public/signups/${m.signup.edit_token}/calendar.ics`}
                      data-testid={`add-to-calendar-${m.signup.id}`}
                      className="inline-flex items-center text-xs font-semibold text-brand-green-dark hover:text-brand-green-dark/80 px-2.5 py-1.5 rounded-md border border-brand-green/30 bg-brand-green/5 hover:bg-brand-green/15 transition-colors"
                    >
                      <CalendarPlus className="w-3.5 h-3.5 mr-1" /> Calendar
                    </a>
                    <Link to={`/my-signup/${m.signup.edit_token}`} className="text-sm font-medium text-brand-blue">
                      Manage →
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {loading && (
          <div className="space-y-4">
            <Skeleton className="h-7 w-48" />
            <Skeleton className="h-64 w-full rounded-xl" />
          </div>
        )}

        {!loading && board?.days?.length === 0 && (
          <div data-testid="empty-board" className="text-center py-16 px-6 bg-white rounded-xl border border-slate-200">
            <p className="font-heading text-2xl text-slate-700 font-semibold">No upcoming Saturdays yet</p>
            <p className="text-sm text-slate-500 mt-2">Check back soon — new shifts are added by your organizer.</p>
          </div>
        )}

        {!loading && board?.days?.length > 0 && (() => {
          const anyMatch = board.days.some((d) =>
            roleFilter === "all" ? d.shifts.length > 0 : d.shifts.some((s) => s.roles?.[roleFilter]?.open > 0)
          );
          if (anyMatch) return null;
          return (
            <div data-testid="empty-role-filter" className="text-center py-14 px-6 bg-white rounded-xl border border-slate-200">
              <p className="font-heading text-xl text-slate-700 font-semibold">No open slots for that role</p>
              <p className="text-sm text-slate-500 mt-2">Every upcoming Saturday is filled for that position — try a different role.</p>
              <button
                data-testid="empty-role-filter-clear"
                onClick={() => setRoleFilter("all")}
                className="mt-4 inline-flex items-center rounded-full bg-brand-blue px-4 py-2 text-xs font-semibold text-white hover:bg-brand-blue-dark focus-ring"
              >
                Show all roles
              </button>
            </div>
          );
        })()}

        {!loading && months.map((m) => {
          const daysInMonth = board.days.filter((d) => monthKeyOf(d.date) === m);
          const days = roleFilter === "all"
            ? daysInMonth
            : daysInMonth
                .map((d) => ({ ...d, shifts: d.shifts.filter((s) => s.roles?.[roleFilter]?.open > 0) }))
                .filter((d) => d.shifts.length > 0);
          if (days.length === 0) return null;
          return (
            <section
              key={m}
              data-testid={`month-section-${m}`}
              data-month={m}
              ref={(el) => { if (el) monthRefs.current[m] = el; }}
              className="space-y-6 scroll-mt-[140px]"
            >
              {days.map((day, di) => (
                <section key={day.date} data-testid={`day-section-${day.date}`} className="space-y-4" style={{ animationDelay: `${di * 60}ms` }}>
                  <div className="flex items-baseline justify-between gap-3 border-b border-slate-200 pb-2">
                    <h2 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
                      <Calendar className="w-5 h-5 text-brand-blue" /> {formatSaturday(day.date)}
                    </h2>
                    <span className="text-xs text-slate-500 font-medium">
                      {day.shifts.length} {day.shifts.length === 1 ? "route" : "routes"}
                    </span>
                  </div>
                  <div className="grid gap-4 sm:gap-5">
                    {day.shifts.map((s) => (
                      <ShiftCard key={s.id} shift={s} mySignupRoleKeys={mySignupRoleKeys} onClaim={setClaimCtx} />
                    ))}
                  </div>
                </section>
              ))}
            </section>
          );
        })}
      </main>

      <ClaimDialog ctx={claimCtx} user={user} open={!!claimCtx} onClose={() => setClaimCtx(null)} onSuccess={handleSuccess} />
      <ConfirmationDialog
        payload={confirm}
        open={!!confirm}
        onClose={() => setConfirm(null)}
        onBackToBoard={() => { setConfirm(null); scrollToMySignups(); }}
      />
      <BulkSignupDialog
        open={bulkOpen}
        onClose={() => setBulkOpen(false)}
        board={board}
        mySignupShiftIds={mySignupShiftIds}
        onSuccess={load}
        onBackToTop={scrollToMySignups}
      />
    </div>
  );
}
